import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ContactUsFormService } from 'src/app/services/contact-us-form.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { SeoService } from 'src/app/services/seo.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-home-page',
    imports: [
        CommonModule,
        RouterModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatDividerModule,
        MatIconModule,
        MatButtonModule,
        MatCardModule,
        MatTooltipModule,
        MatChipsModule,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FontAwesomeModule,
    ],
    templateUrl: './home-page.component.html',
    styleUrl: './home-page.component.scss',
})
export class HomePageComponent implements AfterViewInit, OnInit {
  @Input() data: any;
  @ViewChild('platformRef') platformRef: ElementRef;
  @ViewChild('forYouRef') forYouRef: ElementRef;
  @ViewChild('aboutRef') aboutRef: ElementRef;
  @ViewChild('contactRef') contactRef: ElementRef;

  faApple = faApple;
  faAndroid = faAndroid;

  iosAppLink = environment.links.iosApp;
  androidAppLink = environment.links.androidApp;

  scrolled = signal(false);

  interestedOptions = ['Web platform', 'Mobile app', 'Brokerage', 'Other'];
  interestedIn = signal<string>('Web platform');

  contactUsForm = this.fb.group({
    contactName: ['', Validators.required],
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
      ],
    ],
    companyName: ['', Validators.required],
    phone: ['', Validators.pattern(/^\+?(?:[\s().-]*\d){10,15}[\s().-]*$/)],
    subject: ['Web Platform Contact Us Inquiry'],
    message: [null, Validators.required],
  });

  constructor(
    readonly router: Router,
    private fb: FormBuilder,
    private contactUsService: ContactUsFormService,
    private snackbar: MatSnackBar,
    private seoService: SeoService
  ) {}

  ngOnInit() {
    this.seoService.updateMetaTags([
      {
        name: 'description',
        content: `Johnston's Connected was founded in the fall of 2022 in Calgary, AB,
        with a focus and commitment to bring together trust-worthy commercial buyers and sellers of agricultural commodities,
        allowing for quick and easy access to price discovery and trade execution in the North American grain market`,
      },
      { name: 'author', content: "Johnston's Connected" },
      { name: 'keywords', content: "Johnston's Connected Trading Platform" },
    ]);
    this.seoService.updateTitle("Johnston's Connected Home Page");
  }

  ngAfterViewInit(): void {}

  @HostListener('window:scroll')
  onWindowScroll() {
    this.scrolled.set(window.scrollY > 24);
  }

  private scrollTo(ref: ElementRef | undefined) {
    if (!ref?.nativeElement) return;
    ref.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  scrollToPlatform() {
    this.scrollTo(this.platformRef);
  }

  scrollToForYou() {
    this.scrollTo(this.forYouRef);
  }

  scrollToAbout() {
    this.scrollTo(this.aboutRef);
  }

  scrollToContact() {
    this.scrollTo(this.contactRef);
  }

  selectInterest(option: string) {
    this.interestedIn.set(option);
  }

  showError(controlName: string): boolean {
    const control = this.contactUsForm.get(controlName);
    return control.invalid && (control.touched || control.dirty);
  }

  onSubmit() {
    if (this.contactUsForm.valid) {
      const interest = this.interestedIn();
      const subject = `Landing Page Inquiry — Interest: ${interest}`;
      this.contactUsService
        .mutate({
          input: {
            ...this.contactUsForm.value,
            subject,
            phone: `${this.contactUsForm.get('phone').value}`,
          },
        })
        .subscribe({
          next: (res) => {
            this.contactUsForm.reset();
            this.interestedIn.set('Web platform');
            this.snackbar.open(
              `Thank you for contacting us. We will be in touch with you via email`,
              null,
              {
                duration: 4300,
                panelClass: ['blue-snackbar'],
              }
            );
          },
        });
    } else {
      this.contactUsForm.markAllAsTouched();
      this.snackbar.open(`Unable to send Check form fields`, null, {
        duration: 4300,
        panelClass: ['blue-snackbar'],
      });
    }
  }
}
